import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, Calendar } from 'lucide-react';

interface TimeSlot {
  id: string;
  time: string;
  available: boolean;
  booked: boolean;
}

interface Availability {
  date: string;
  slots: TimeSlot[];
}

interface BookingCalendarProps {
  onTimeSelect: (date: string, time: string) => void;
  selectedDate?: string;
  selectedTime?: string;
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  onTimeSelect,
  selectedDate,
  selectedTime
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [loading, setLoading] = useState(true);

  // Mock availability data - in real app, this would come from your backend
  const mockAvailability: Availability[] = [
    {
      date: new Date().toISOString().split('T')[0],
      slots: [
        { id: '1', time: '09:00', available: true, booked: false },
        { id: '2', time: '10:00', available: true, booked: false },
        { id: '3', time: '11:00', available: false, booked: false },
        { id: '4', time: '14:00', available: true, booked: false },
        { id: '5', time: '15:00', available: true, booked: false },
        { id: '6', time: '16:00', available: false, booked: false },
      ]
    },
    {
      date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      slots: [
        { id: '7', time: '09:00', available: true, booked: false },
        { id: '8', time: '10:00', available: true, booked: false },
        { id: '9', time: '11:00', available: true, booked: false },
        { id: '10', time: '14:00', available: true, booked: false },
        { id: '11', time: '15:00', available: true, booked: false },
        { id: '12', time: '16:00', available: true, booked: false },
      ]
    },
    {
      date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      slots: [
        { id: '13', time: '09:00', available: true, booked: false },
        { id: '14', time: '10:00', available: false, booked: false },
        { id: '15', time: '11:00', available: true, booked: false },
        { id: '16', time: '14:00', available: true, booked: false },
        { id: '17', time: '15:00', available: true, booked: false },
        { id: '18', time: '16:00', available: true, booked: false },
      ]
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setAvailability(mockAvailability);
      setLoading(false);
    }, 1000);
  }, []);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateAvailable = (day: number | null) => {
    if (!day) return false;
    const dateStr = formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
    return availability.some(avail => avail.date === dateStr && avail.slots.some(slot => slot.available));
  };

  const isDateSelected = (day: number | null) => {
    if (!day) return false;
    const dateStr = formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
    return selectedDate === dateStr;
  };

  const getTimeSlotsForDate = (dateStr: string) => {
    const dayAvailability = availability.find(avail => avail.date === dateStr);
    return dayAvailability ? dayAvailability.slots : [];
  };

  const handleDateClick = (day: number) => {
    const dateStr = formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
    if (isDateAvailable(day)) {
      onTimeSelect(dateStr, '');
    }
  };

  const handleTimeClick = (time: string) => {
    if (selectedDate) {
      onTimeSelect(selectedDate, time);
    }
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
          <Calendar className="h-5 w-5 mr-2" />
          Select Date & Time
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-lg font-medium min-w-[140px] text-center">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-6">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}
        {days.map((day, index) => (
          <button
            key={index}
            onClick={() => day && handleDateClick(day)}
            disabled={!day || !isDateAvailable(day)}
            className={`
              p-2 text-center text-sm rounded-lg transition-colors
              ${!day ? 'invisible' : ''}
              ${!isDateAvailable(day) ? 'text-gray-300 cursor-not-allowed' : ''}
              ${isDateSelected(day) ? 'bg-red-600 text-white' : ''}
              ${day && isDateAvailable(day) && !isDateSelected(day) ? 'hover:bg-red-50 text-gray-700' : ''}
            `}
          >
            {day}
          </button>
        ))}
      </div>

      {selectedDate && (
        <div className="border-t pt-6">
          <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
            <Clock className="h-4 w-4 mr-2" />
            Available Times for {new Date(selectedDate).toLocaleDateString()}
          </h4>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {getTimeSlotsForDate(selectedDate).map(slot => (
              <button
                key={slot.id}
                onClick={() => handleTimeClick(slot.time)}
                disabled={!slot.available || slot.booked}
                className={`
                  p-3 text-sm rounded-lg border transition-colors
                  ${!slot.available || slot.booked 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' 
                    : selectedTime === slot.time
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-red-50 hover:border-red-300'
                  }
                `}
              >
                {slot.time}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCalendar;
